import React, { Component } from 'react';
import { withRouter, RouteComponentProps, Route, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';

// Components
import LegacySettings from '@/views/containers/settings/LegacySettings';
import SettingsBilling from '@/views/containers/settings/SettingsBilling';
import SettingsContact from '@/views/containers/settings/SettingsContact';
import SettingsDelegates from '@/views/containers/settings/SettingsDelegates';
import SettingsPreferences from '@/views/containers/settings/SettingsPreferences';
import SettingsSecurity from '@/views/containers/settings/SettingsSecurity';
import { ErrorBoundary } from '@/views/components/errors';
import { Header } from '@/views/components/layout';
import { SecondaryNavigation } from '@/views/components/navigation';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Redux
import { getSubscriptionInfos, getCheckoutHostedPage, getChargebeePortalSession } from '@/redux/modules/subscription';
import { ReduxState } from '@/redux/modules/types';
import { selectDelegateMode } from '@/redux/modules/auth/selectors';
import { setLocale } from '@/redux/modules/intl';
import { updateMe, updatePassword, updatePreferences } from '@/redux/modules/auth';

// Types
import AppRegion from '@/types/app-region';
import EditableUserInfos from '@/types/editable-user-infos';
import Locale from '@/types/locale';
import Subscription from '@/types/subscription';
import User from '@/types/user';
import UserPreferences from '@/types/user-preferences';
import UserPreferencesComparisonSettings from '@/types/user-preferences-comparison-settings';

// Utils
import ChargebeeUtils, { SUBSCRIPTION_PLAN } from '@/utils/3rd-party/chargebee';
import notification from '@/utils/notification';
import ZendeskUtils from '@/utils/3rd-party/zendesk';
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { getInitialUserPreferences } from '@/utils/users';
import { userHasPermission, PERMISSIONS } from '@/utils/permissions';

enum SettingsTabs {
  CONTACT = '/settings/contact',
  PREFERENCES = '/settings/preferences',
  SECURITY = '/settings/security',
  BILLING = '/settings/billing',
  GROUPS = '/settings/groups',
  DELEGATES = '/settings/delegates',
}

type StateProps = {
  user: User;
  locale: Locale;
  resetPortfolio: Function;
  subscription?: Subscription;
  delegateMode: boolean;
  loading: {
    subscription: boolean;
  };
  comparisonSettings?: UserPreferencesComparisonSettings;
};

type DispatchProps = {
  updateMe: (infos: EditableUserInfos) => Promise<User>;
  updatePassword: (current: string, password: string, confirm: string) => Promise<void>;
  getChargebeePortalSession: () => Promise<any>;
  getCheckoutHostedPage: (plan: string) => Promise<any>;
  setLocale: (locale: Locale) => void;
  getSubscriptionInfos: () => Promise<Subscription>;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
};

type Props = RouteComponentProps & StateProps & DispatchProps;
class SettingsV2 extends Component<Props> {
  componentDidMount() {
    this.props.getSubscriptionInfos();
  }

  setComparisonSettings = () => {
    const { user, comparisonSettings } = this.props;
    return { ...getInitialUserPreferences(user), ...comparisonSettings };
  };

  getSettingsRoutes = () => {
    const { user, match, subscription, delegateMode } = this.props;
    const appRegion: AppRegion = user != null ? user.appConfig.appRegion : (AppRegion.CAN as AppRegion);

    return [
      {
        value: SettingsTabs.CONTACT,
        label: formatMessage('Settings.Nav.Contact'),
        hidden: !userHasPermission(user, PERMISSIONS.DELEGATE_CONTACT_SETTINGS) && delegateMode,
        path: `${match.path}/contact`,
        render: (props: RouteComponentProps) => <SettingsContact {...props} user={user} onSave={this.onSaveContactInfos} />,
      },
      {
        value: SettingsTabs.PREFERENCES,
        label: formatMessage('Settings.Nav.Preferences'),
        path: `${match.path}/preferences`,
        render: (props: RouteComponentProps) => (
          <SettingsPreferences
            {...props}
            user={user}
            locale={this.props.locale}
            onLocaleChange={this.onLocaleChange}
            delegateMode={delegateMode}
            comparisonSettings={this.setComparisonSettings()}
            onComparisonSettingsChange={this.onComparisonSettingsChange}
          />
        ),
      },
      {
        value: SettingsTabs.SECURITY,
        label: formatMessage('Settings.Nav.Security'),
        // Hidden for SSO users
        hidden: this.props.user.nameId != null,
        path: `${match.path}/security`,
        render: (props: RouteComponentProps) => <SettingsSecurity {...props} user={user} onUpdatePassword={this.onUpdatePassword} />,
      },
      {
        value: SettingsTabs.BILLING,
        label: formatMessage('Settings.Nav.Billing'),
        hidden: delegateMode || appRegion !== AppRegion.CAN || !!user.enterprise || !subscription,
        path: `${match.path}/billing`,
        render: (props: RouteComponentProps) => (
          <SettingsBilling
            {...props}
            loading={this.props.loading.subscription}
            onUpgradeToPro={this.onUpgradeToPro}
            onCancelSubscription={this.onCancelSubscription}
            onOpenCustomerPortal={this.onOpenCustomerPortal}
            subscription={subscription}
          />
        ),
      },
      {
        value: SettingsTabs.DELEGATES,
        label: formatMessage('Settings.Nav.Delegates'),
        hidden: !userHasPermission(this.props.user, PERMISSIONS.DELEGATE_MANAGEMENT) || delegateMode,
        path: `${match.path}/delegates`,
        render: (props: RouteComponentProps) => <SettingsDelegates user={user} {...props} />,
      },
    ].filter(route => !route.hidden);
  };

  onUpdatePassword = (current: string, password: string, confirm: string) => this.props.updatePassword(current, password, confirm);

  onOpenCustomerPortal = () => {
    this.props.getChargebeePortalSession().then(async (session: any) => {
      ChargebeeUtils.openPortal(session);
    });
  };

  onUpgradeToPro = () => {
    ChargebeeUtils.getInstance().openCheckout({
      hostedPage: () => this.props.getCheckoutHostedPage(SUBSCRIPTION_PLAN.PRO_MONTHLY),
    });
  };

  onCancelSubscription = () => {
    const { user } = this.props;

    this.props.getChargebeePortalSession().then(async (session: any) => {
      ChargebeeUtils.openPortal(session, {
        sectionType: ChargebeeUtils.getPortalSections().SUBSCRIPTION_CANCELLATION,
        params: {
          subscriptionId: user.chargeBeeId,
        },
      });
    });
  };

  onSaveContactInfos = (infos: EditableUserInfos) => this.props.updateMe(infos);

  onLocaleChange = async (locale: Locale) => {
    if (locale === this.props.locale) {
      return;
    }

    this.props.setLocale(locale);
    await this.props.updatePreferences({ locale });
    ZendeskUtils.setLocale(locale);

    // Use setTimeout to make sure language has been changed before sending the notification.
    setTimeout(() => {
      notification.success(formatMessage('Settings.Preferences.LanguageChanged'));
    }, 0);
  };

  onComparisonSettingsChange = async (option: { [key: string]: string }) => {
    await this.props.updatePreferences({ comparisonSettings: { ...option } });

    // Use setTimeout to make sure settings have been changed before sending the notification.
    setTimeout(() => {
      notification.success(formatMessage('Settings.Preferences.SettingChanged'));
    }, 0);
  };

  render() {
    const { match } = this.props;

    const routes = this.getSettingsRoutes();

    return (
      <div className="settings-v2">
        <Header />
        <main id="content" className="settings-container">
          <div className="settings-body">
            <div className="settings-header">
              <h3 className="settings-header-title">
                <FormattedMessage id="Settings.PageTitle" />
              </h3>
              <SecondaryNavigation className="settings-header-navigation" items={routes} />
            </div>
            <div className="settings-content">
              <ErrorBoundary>
                {routes.map(route => (
                  <Route key={route.value} path={route.path} render={route.render} />
                ))}
                <Route exact path={`${match.path}/`} render={() => <Redirect to={routes[0].path} />} />
              </ErrorBoundary>
            </div>
          </div>
        </main>
      </div>
    );
  }
}

const Settings: React.FC<Props> = props => {
  const { isLightVersion } = useThemeVersion();

  if (isLightVersion) {
    return <SettingsV2 {...props} />;
  }

  return <LegacySettings {...props} />;
};

const mapStateToProps = (state: ReduxState) => ({
  locale: state.intl.locale,
  subscription: state.subscription.subscription,
  delegateMode: selectDelegateMode(state),
  loading: {
    subscription: state.subscription.loading,
  },
  comparisonSettings: state.auth.user?.preferences?.comparisonSettings,
});

export default withRouter(
  connect(mapStateToProps, {
    updateMe,
    updatePassword,
    getChargebeePortalSession,
    setLocale,
    getCheckoutHostedPage,
    getSubscriptionInfos,
    updatePreferences,
  })(Settings),
);
